<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { formatAddress, formatIdSource } from "@core/helpers"
import type { Address } from "@types"

// ================================
// TYPES & INTERFACES
// ================================

/** Type de demande de consentement pour le message de succès */
export type ConsentSignSuccessRequestType = "SIGN" | "EXTEND"

/** Type de compteur (PDL électricité, PCE gaz) */
export type ConsentSignSuccessMeterType = "pdl" | "pce"

/** Adresse acceptée par formatAddress (sans citycode, inline_name, country) */
type AddressForFormat = Omit<Address, "citycode" | "inline_name" | "country">

export interface SourceConsentSignSuccessMessageProps {
    /** Type de demande : signature ou prolongation */
    requestType: ConsentSignSuccessRequestType
    /** Type de compteur (pdl = électricité, pce = gaz) */
    meterType: ConsentSignSuccessMeterType
    /** Numéro PDL ou PCE */
    idsource: string
    /** Adresse du point de livraison / comptage */
    address?: AddressForFormat | null
}

// ================================
// PROPS
// ================================

const props = withDefaults(
    defineProps<SourceConsentSignSuccessMessageProps>(),
    {
        address: null,
    }
)

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            success_title: "Consentement signé",
            success_title_extend: "Consentement prolongé",
            success_message:
                "Vous venez de signer le consentement nécessaire pour autoriser l'accès aux données du compteur {type} numéro {idsource} situé au {address}.",
            success_message_extend:
                "Vous venez de prolonger le consentement nécessaire pour autoriser l'accès aux données du compteur {type} numéro {idsource} situé au {address}.",
        },
    },
})

// ================================
// COMPUTED
// ================================

/** Titre du message selon le type de demande */
const getSuccessTitle = computed(() =>
    props.requestType === "EXTEND"
        ? t("success_title_extend")
        : t("success_title")
)

/** Libellé du type de compteur (d'électricité / de gaz) */
const getMeterTypeLabel = computed(() =>
    props.meterType === "pce" ? "de gaz" : "d'électricité"
)

/** Identifiant source formaté (espaces PDL/PCE) */
const getIdsourceFormatted = computed(() =>
    formatIdSource(props.idsource, props.meterType)
)

/** Adresse formatée */
const getAddressFormatted = computed(() =>
    formatAddress(props.address ?? undefined)
)

/** Message de succès avec paramètres interpolés */
const getSuccessMessage = computed(() => {
    const msgKey =
        props.requestType === "EXTEND"
            ? "success_message_extend"
            : "success_message"
    return t(msgKey, {
        type: getMeterTypeLabel.value,
        idsource: getIdsourceFormatted.value,
        address: getAddressFormatted.value,
    })
})
</script>

<template>
    <BaseMessage severity="success" class="w-full">
        <div class="flex flex-col gap-8">
            <h3
                class="font-semibold text-lg text-center flex items-center gap-2 mx-auto"
            >
                {{ getSuccessTitle }}
                <BaseIcon icon="pi pi-check" />
            </h3>
            <p>{{ getSuccessMessage }}</p>
        </div>
    </BaseMessage>
</template>
